import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './ContextMenu.module.css';

export type ContextMenuItem =
  | { type?: 'action'; label: string; onClick: () => void; danger?: boolean }
  | { type: 'separator' };

interface ContextMenuProps {
  x: number;
  y: number;
  actions: ContextMenuItem[];
  onClose: () => void;
}

const EDGE = 8;

export function ContextMenu({ x, y, actions, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Clamp to the viewport before first paint: pull inside the right edge,
  // flip above the cursor when the menu doesn't fit below.
  useLayoutEffect(() => {
    const rect = ref.current!.getBoundingClientRect();
    const left = Math.max(EDGE, Math.min(x, window.innerWidth - rect.width - EDGE));
    let top = y;
    if (y + rect.height > window.innerHeight - EDGE) {
      top = y - rect.height >= EDGE ? y - rect.height : window.innerHeight - rect.height - EDGE;
    }
    setPos({ left, top: Math.max(EDGE, top) });
  }, [x, y]);

  // ARIA menu focus contract: focus moves to the first item on open and back
  // to whatever had it (the trigger) on close.
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => trigger?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose]);

  // Roving focus: ArrowUp/ArrowDown wrap across the items, Home/End jump,
  // Tab closes (focus restores to the trigger via the mount effect above).
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      onClose();
      return;
    }
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(idx + 1) % items.length].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[idx <= 0 ? items.length - 1 : idx - 1].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1].focus();
    }
  };

  return (
    <div ref={ref} className={styles.menu} style={{ left: pos.left, top: pos.top }} role="menu" onKeyDown={handleKeyDown}>
      {actions.map((item, i) => {
        if (item.type === 'separator') {
          return <div key={`sep-${i}`} className={styles.separator} role="separator" />;
        }
        return (
          <button
            key={item.label}
            className={`${styles.item} ${item.danger ? styles.danger : ''}`}
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              item.onClick();
              onClose();
            }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
